import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { galleryAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Package, 
  LogOut, 
  Trash2,
  ShoppingBag,
  RefreshCw,
  Plus,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const AdminGalleryPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    date: '',
    image: '',
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    loadGallery();
  }, [user, navigate]);

  const loadGallery = async () => {
    try {
      setLoading(true);
      const data = await galleryAPI.getAll();
      setGallery(data);
    } catch (error) {
      toast.error('Ошибка загрузки галереи');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const result = await galleryAPI.uploadImage(file);
      
      setFormData({
        ...formData,
        image: result.image_url,
      });
      
      toast.success('Изображение загружено');
    } catch (error) {
      toast.error('Ошибка загрузки изображения');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.location || !formData.date || !formData.image) {
      toast.error('Заполните все поля');
      return;
    }

    try {
      await galleryAPI.create(formData);
      toast.success('Элемент галереи добавлен');
      resetForm();
      loadGallery();
    } catch (error) {
      toast.error('Ошибка добавления элемента');
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Удалить этот элемент из галереи?')) {
      return;
    }
    
    try {
      await galleryAPI.delete(itemId);
      toast.success('Элемент удален');
      loadGallery();
    } catch (error) {
      toast.error('Ошибка удаления');
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setFormData({
      title: '',
      location: '',
      date: '',
      image: '',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Админ-панель</h1>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/orders')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Заказы
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/products')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Товары
                </Button>
                <Button
                  variant="outline"
                  className="border-emerald-600 text-emerald-700"
                  onClick={() => navigate('/admin/gallery')}
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Галерея
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/reviews')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Отзывы
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {user?.username}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Выйти
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {!showForm ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Галерея работ</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadGallery}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Обновить
                  </Button>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить фото
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-emerald-600 mb-4" />
                  <p className="text-gray-600">Загрузка...</p>
                </div>
              ) : gallery.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Галерея пуста</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gallery.map((item) => (
                    <div key={item.id} className="relative group">
                      <img 
                        src={item.image} 
                        alt={item.title}
                        className="w-full h-64 object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all rounded-lg flex items-center justify-center">
                        <Button
                          variant="destructive"
                          size="sm"
                          className="opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Удалить
                        </Button>
                      </div>
                      <div className="mt-2">
                        <h3 className="font-semibold">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.location}</p>
                        <p className="text-xs text-gray-500">{item.date}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Добавить фото в галерею</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetForm}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Image Upload */}
                <div className="space-y-2">
                  <Label>Изображение *</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                  />
                  {uploadingImage && (
                    <p className="text-sm text-gray-600">Загрузка...</p>
                  )}
                  {formData.image && (
                    <img 
                      src={formData.image} 
                      alt="Preview"
                      className="w-full h-64 object-cover rounded-lg mt-2"
                    />
                  )}
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label htmlFor="title">Название *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Например: Теплица 'Премиум' в Уфе"
                    required
                  />
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label htmlFor="location">Местоположение *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Например: г. Уфа"
                    required
                  />
                </div>

                {/* Date */}
                <div className="space-y-2">
                  <Label htmlFor="date">Дата *</Label>
                  <Input
                    id="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    placeholder="Например: Июнь 2026"
                    required
                  />
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="bg-emerald-600 hover:bg-emerald-700"
                    disabled={!formData.image}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить в галерею
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                  >
                    Отмена
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminGalleryPage;
